import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { format, isAfter, isBefore, parse } from 'date-fns';
import { ExecutionResult } from 'graphql';
import { IntlFormatters, useIntl } from 'react-intl';

import { IServerOrder } from '@rbi-ctg/menu';
import { IStore } from '@rbi-ctg/store';
import { ThreeDSChallengeModal } from 'components/three-ds-challenge-modal';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import { useGetRestaurantsQuery } from 'generated/graphql-gateway';
import { ICommitOrderInput, useCommitOrder } from 'hooks/commit-order';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import { ThreeDsMethod } from 'hooks/use-threeDS';
import { useIOSLiveActivities } from 'pages/cart/hooks/use-ios-live-activities';
import { ICommitOrderMutationResponse } from 'remote/queries/order';
import { IValidDayPart } from 'state/day-part/hooks/use-active-day-parts';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { DAY_PART_SELECTIONS } from 'state/main-menu/types';
import { OrderStatus, useOrderContext } from 'state/order';
import { usePaymentContext } from 'state/payment';
import { CASH_ACCOUNT_IDENTIFIER } from 'state/payment/constants';
import { ThreeDSDataPayment, ThreeDSType } from 'state/payment/hooks/types';
import { IPaymentDetails } from 'state/payment/types';
import { ServiceMode } from 'state/service-mode';
import {
  GraphQLErrorCodes,
  getErrorDialogForGraphQLCode,
  parseGraphQLErrorCodes,
} from 'utils/errors';
import { IFraudPreventionValues, splitExpiry } from 'utils/payment';
import { readableCloseHourToday } from 'utils/restaurant';
import { DAYPART_FORMAT, TWELVE_HOUR_TIME_PARSE_FORMAT } from 'utils/restaurant/constants';
import { routes } from 'utils/routing';
import { getThreeDSError } from 'utils/threeDS';

import { CardButton } from './card-button';

type onCommitType = (
  input: ICommitOrderInput
) => Promise<ExecutionResult<ICommitOrderMutationResponse>>;

type onCommitErrorType = (error: any) => void;

type ThreeDSState = ThreeDSDataPayment & { challengeResponse?: string };

const Pickup = Text.withConfig({
  color: Styles.color.accent,
});

const handleCommitOrder = (
  onCommit: onCommitType,
  onCommitError: onCommitErrorType,
  payment: {
    clearPaymentDetails(): void;
    paymentDetails: IPaymentDetails;
    threeDS: ThreeDSState | null;
  },
  serviceMode: ServiceMode,
  skipCoolingPeriod?: boolean
) => {
  const { paymentDetails, clearPaymentDetails, threeDS } = payment;
  const {
    accountIdentifier,
    creditType,
    fdAccessToken,
    fdNonce,
    fraudPreventionValues = {} as IFraudPreventionValues,
    applePayDetails,
    googlePayDetails,
  } = paymentDetails;

  let commitInput: ICommitOrderInput;

  // check for cash payments
  if (accountIdentifier === CASH_ACCOUNT_IDENTIFIER) {
    commitInput = {
      creditType: CASH_ACCOUNT_IDENTIFIER,
      serviceMode,
      skipCoolingPeriod,
      payment: {
        cashPayment: true,
        fullName: fraudPreventionValues.fullName,
      },
    };

    return onCommit(commitInput).then(() => {
      clearPaymentDetails();
    });
  }

  const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

  return onCommit({
    applePayDetails,
    creditType,
    fdAccountId: accountIdentifier,
    fullName: fraudPreventionValues.fullName,
    serviceMode,
    payment: {
      billingAddress: fraudPreventionValues.billingAddress,
      ccMetadata: {
        ccBin: fraudPreventionValues.ccBin,
        ccExpiryMonth: expiryMonth,
        ccExpiryYear: expiryYear,
        ccLast4: fraudPreventionValues.ccLast4,
      },
      firstDataInput: {
        accountIdentifier,
        fdAccessToken,
        fdNonce,
      },
      fullName: fraudPreventionValues.fullName,
    },
    googlePayDetails,
    skipCoolingPeriod: threeDS ? true : skipCoolingPeriod,
    threeDSDetails: threeDS
      ? {
          saleId: threeDS.saleId!,
          threeDSOptions: {
            challengeResponse: threeDS.challengeResponse,
            transactionId: threeDS.transactionId!,
          },
        }
      : undefined,
  })
    .then(() => {
      clearPaymentDetails();
    })
    .catch(onCommitError);
};

interface IGetHoursText {
  dayParts: readonly IValidDayPart[];
  formatMessage: IntlFormatters['formatMessage'];
  hours: Pick<IStore, 'driveThruHours' | 'diningRoomHours'>;
  orderedAt: Date;
  serviceMode: ServiceMode;
}

// TODO: RN - locale switching -- can we get rid of this? only used in test files?
// if not, will need to update to accept timeFormatConfig prop to pass to readableCloseHourToday
// e.x const timeFormatConfig = useConfigValue({ key: 'timeFormat' });
export const getHoursText = ({
  dayParts,
  formatMessage,
  hours,
  orderedAt,
  serviceMode,
}: IGetHoursText) => {
  const { startTime, endTime } =
    dayParts.find(dayPart => dayPart.key === DAY_PART_SELECTIONS.BREAKFAST_KEY) || {};
  const breakfastStartTime = startTime ? parse(startTime, DAYPART_FORMAT, new Date()) : null;
  const breakfastEndTime = endTime ? parse(endTime, DAYPART_FORMAT, new Date()) : null;

  const isBreakfastOrder =
    breakfastStartTime && breakfastEndTime
      ? isAfter(orderedAt, breakfastStartTime) && isBefore(orderedAt, breakfastEndTime)
      : false;

  const serviceModeHours =
    serviceMode === ServiceMode.DRIVE_THRU ? hours.driveThruHours : hours.diningRoomHours;

  const pickupTime =
    isBreakfastOrder && breakfastEndTime
      ? format(breakfastEndTime, TWELVE_HOUR_TIME_PARSE_FORMAT)
      : readableCloseHourToday(serviceModeHours);

  return formatMessage(
    { id: 'pickUpBefore' },
    {
      pickupTime,
      pickup: str => <Pickup>{str}</Pickup>,
    }
  );
};

export function handleThreeDSError(
  error: any,
  onThreeDSError: (data: ThreeDSDataPayment | null) => void
): void {
  const threeDSError = getThreeDSError(error);
  if (threeDSError) {
    onThreeDSError(threeDSError);
    return;
  }
  onThreeDSError(null);
  throw error;
}

const PickupCard = ({
  confirmed = false,
  serverOrder,
}: {
  confirmed?: boolean;
  serverOrder: IServerOrder;
}) => {
  const { onCommitSuccess } = useOrderContext();
  const { emptyCart } = useCart();
  const { navigate } = useNavigation();
  const [threeDS, setThreeDS] = useState<ThreeDSState | null>(null);
  const threeDSChallengeFinished = useRef(false);

  const { serviceMode } = serverOrder.cart;

  const { commitOrder, loading, orderStatus, commitResult } = useCommitOrder({
    rbiOrderId: serverOrder.rbiOrderId,
  });

  const { data: restaurantsData, loading: loadingRestaurant } = useGetRestaurantsQuery({
    variables: {
      input: {
        first: 1,
        coordinates: {
          userLat: serverOrder.cart.storeDetails.latitude,
          userLng: serverOrder.cart.storeDetails.longitude,
          searchRadius: 20,
        },
      },
    },
  });

  const { clearPaymentDetails, paymentDetails } = usePaymentContext();
  const onCommitError = (err: any) => handleThreeDSError(err, setThreeDS);

  const onCommitOrderSkipCooldown = useCallback(() => {
    const payment = {
      clearPaymentDetails,
      paymentDetails,
      threeDS,
    };

    handleCommitOrder(commitOrder, onCommitError, payment, serviceMode, true).then(() => {
      onCommitSuccess(serverOrder);
      return emptyCart();
    });
  }, [
    clearPaymentDetails,
    commitOrder,
    emptyCart,
    onCommitSuccess,
    paymentDetails,
    serverOrder,
    serviceMode,
    threeDS,
  ]);

  const { updateActivity } = useIOSLiveActivities();

  const onCommitOrder = useCallback(async () => {
    const payment = {
      clearPaymentDetails,
      paymentDetails,
      threeDS,
    };

    await handleCommitOrder(commitOrder, onCommitError, payment, serviceMode, undefined);
    onCommitSuccess(serverOrder);
    updateActivity(serverOrder.rbiOrderId, 'IN_PROGRESS');
    return emptyCart();
  }, [
    clearPaymentDetails,
    commitOrder,
    emptyCart,
    onCommitSuccess,
    paymentDetails,
    serverOrder,
    serviceMode,
    threeDS,
  ]);

  const onCancelModal = () => navigate(routes.orders);
  const set3DSChallengeResponse = (cRes: string) => {
    threeDSChallengeFinished.current = true;
    setThreeDS({ ...threeDS, challengeResponse: cRes } as ThreeDSState);
  };
  const [OrderCooldownErrorDialog, openOrderCooldownErrorDialog] = useDialogModal({
    onConfirm: onCommitOrderSkipCooldown,
    onCancel: onCancelModal,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation Modal: Store Location',
  });

  const { formatMessage } = useIntl();
  const orderIsLoading = loading || orderStatus === OrderStatus.INSERT_REQUESTED;
  const enableImCloseDisplay = useFlag(LaunchDarklyFlag.ENABLE_IM_CLOSE_DISPLAY);

  const shouldShowImCloseText = enableImCloseDisplay && serviceMode !== ServiceMode.CURBSIDE;

  const screenReaderButtonName = shouldShowImCloseText ? 'imClose' : 'imHere';

  useEffect(() => {
    if (!commitResult.error || getThreeDSError(commitResult.error)) {
      return;
    }

    const [parsedGqlError] = parseGraphQLErrorCodes(commitResult.error);
    if (!parsedGqlError) {
      return;
    }

    const mappedErrorCode = getErrorDialogForGraphQLCode(parsedGqlError, formatMessage);
    const isCommitOrderCoolingPeriodError =
      parsedGqlError.errorCode === GraphQLErrorCodes.COMMIT_ORDER_COOLING_PERIOD;

    if (mappedErrorCode && isCommitOrderCoolingPeriodError) {
      const { message, title = '' } = mappedErrorCode;
      openOrderCooldownErrorDialog({ message, title });
    }
  }, [commitResult, formatMessage, openOrderCooldownErrorDialog]);

  useEffect(() => {
    if (threeDSChallengeFinished.current) {
      threeDSChallengeFinished.current = false;
      onCommitOrderSkipCooldown();
    }
  }, [onCommitOrderSkipCooldown]);

  return (
    <>
      <Box testID="pickup-card">
        <VisuallyHidden
          role="alert"
          accessibilityLabel={formatMessage(
            { id: 'pickUpConfirmationScreenReaderInstructions' },
            {
              screenReaderButtonName,
            }
          )}
        />
      </Box>
      {!loadingRestaurant && !!restaurantsData?.restaurants?.nodes[0] && (
        <>
          {!confirmed && (
            <CardButton
              isLoading={orderIsLoading}
              onCommit={onCommitOrder}
              orderStatus={orderStatus}
              restaurant={restaurantsData?.restaurants?.nodes[0]}
              shouldShowImCloseText={shouldShowImCloseText}
            />
          )}
        </>
      )}

      <OrderCooldownErrorDialog
        confirmLabel={formatMessage({ id: 'placeOrder' })}
        cancelLabel={formatMessage({ id: 'recentOrders' })}
      />
      {threeDS && threeDS.type === ThreeDSType.METHOD && !!threeDS.iframeContent && (
        <ThreeDsMethod
          iframeContent={threeDS.iframeContent}
          onComplete={onCommitOrderSkipCooldown}
        />
      )}
      {threeDS &&
        threeDS.type === ThreeDSType.CHALLENGE &&
        !!threeDS.acsUrl &&
        !!threeDS.challengeRequestToken && (
          <ThreeDSChallengeModal
            acsUrl={threeDS.acsUrl}
            challengeRequestToken={threeDS.challengeRequestToken}
            onChallengeSuccess={set3DSChallengeResponse}
            headerIdMessage={'paymentVerification'}
          />
        )}
    </>
  );
};

export default memo(PickupCard);
